import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GameEngineService, GameService, InternationalizationService, SessionService, TransitionService, User } from '@app/core/services';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { JeopardyGame, JeopardyRound, JeopardyUser } from '@app/shared/graphql/types';

@Component({
    selector: 'jeopardy-join-room',
    templateUrl: './join-room.component.html',
    styleUrls: ['./join-room.component.scss'],
    imports: [RouterLink, FormsModule]
})
export class JoinRoomComponent implements OnInit {
  public host: JeopardyUser;
  public accessCode: string;
  public currentUser: User;
  public joinError: string;
  private subscriptions: Subscription;
  public gameLoaded: boolean;
  public players: JeopardyUser[];
  public room: JeopardyGame;

  public i18n = inject(InternationalizationService);
  public route = inject(ActivatedRoute);
  public gameService = inject(GameService);
  public sessionService = inject(SessionService);
  public router = inject(Router);
  public transitionService = inject(TransitionService);
  public gameEngineService = inject(GameEngineService);

  ngOnInit(): void {
    this.subscriptions = new Subscription();
    this.joinError = '';
    this.currentUser = this.sessionService.getUser();
    this.accessCode = '';
    const gameId = +this.route.snapshot.paramMap.get('id');
    this.gameLoaded = false;
    

    if(gameId) {
      const initGameSub$ = this.gameEngineService.init(gameId).pipe(
        tap(room => {
          console.log(room);
          this.room = room;
          this.gameLoaded = true;
          
          this.players = this.gameEngineService.players.filter(player => !player.host);
          console.log(this.players);

          this.host = this.gameEngineService.players.filter((player) => player.host)[0] || null;

          this.startGame();
        })
      )
      .subscribe();
      this.subscriptions.add(initGameSub$);

      const userConnectedSub$ = this.gameService.getUserConnectedSubscription(gameId)
      .pipe(
        tap(connectedUser => {
          const currentDevice = this.gameService.getDeviceDetails();

          const userUpdate = {
            ...connectedUser,
            name: this.currentUser.username,
            connected: connectedUser.connected,
            language: this.currentUser.location.language.isoCode2Letters,
            location: this.currentUser.location.country.countryId,
            deviceDetected: currentDevice[0],
            deviceIdentifier: currentDevice[1],
            score: connectedUser.score,
            avatar: this.currentUser.avatar.split('/').pop(),
            host: false,
            jokerFiftyFifty: connectedUser.jokerFiftyFifty || 0,
            jokerSkip: connectedUser.jokerFiftyFifty || 0
          };

          const updateUserSub$ = this.gameService.updateUser(userUpdate).subscribe(_ => {
            
            this.gameService.setGameId(this.room.gameId);
            this.sessionService.updateUserGameId(this.room.gameId);
  
            const loadPlayersSub$ = this.gameEngineService.loadPlayers().subscribe(_ =>{
              if(this.gameEngineService.players.length === 2) {
                this.players = this.gameEngineService.players;
              } else {
                this.players = this.gameEngineService.players.filter(player => !player.host);
              }

              this.startGame();
            });

            this.subscriptions.add(loadPlayersSub$);
          });

          this.subscriptions.add(updateUserSub$);
        })
      )
      .subscribe();

      this.subscriptions.add(userConnectedSub$);
    }
  }

  private startGame() {
    const connectedPlayers = this.players.filter(player => player.connected);

    let playerIsConnected = false;

    if(this.gameEngineService.connectedPlayer) {
      playerIsConnected = this.players.filter(player => player.userId === this.gameEngineService.connectedPlayer.userId).length > 0;
    }

    if(connectedPlayers.length !== this.players.length ) {
      if(!playerIsConnected) {
        return;
      }
      this.router.navigate(['/multiplayer/lobby', this.room.gameId]);
      return;
    }

    this.transitionService.showGameLoader('Loading the game...');

    const gameRound$ = this.gameService.getGameRound(this.room.gameId).subscribe(round => {
      setTimeout(() => {
        const isRoundActiveUser = this.checkRoundActiveUser(round);

        if(isRoundActiveUser) {
          this.router.navigate(['select-topic']);
          setTimeout(() => {
            this.transitionService.hideGameLoader();
          }, 500);
        } else {
          this.transitionService.gameLoaderMessage$.next(`Waiting for ${round.activeUser.name} to select topic`);
        }
      }, 1500);
    });

    this.subscriptions.add(gameRound$);
  }

  public checkRoundActiveUser(round: JeopardyRound): boolean {
    const deviceDetails = this.gameService.getDeviceDetails();

    const sameLanguage = round.activeUser.language === this.currentUser.location.language.isoCode2Letters;
    const sameLocation = round.activeUser.location === this.currentUser.location.country.countryId;
    const sameName = round.activeUser.name === this.currentUser.username;
    const sameDeviceType = round.activeUser.deviceDetected === deviceDetails[0];
    const sameDeviceId = round.activeUser.deviceIdentifier === deviceDetails[1];

    return sameLanguage && sameLocation && sameName && sameDeviceType && sameDeviceId;
  }

  public joinRoom(ev: Event, room: JeopardyGame) {
    ev.preventDefault();

    let params = {
      accessCode: this.accessCode,
      lang: this.currentUser.location.language.isoCode2Letters,
      gameId: room.gameId
    };

    this.gameService.joinRoom(params.accessCode, params.lang, params.gameId).subscribe(data => {
      this.joinError = '';

      if(data.errors || !data.entity) {
        this.joinError = this.i18n.str('challengeu.mp_join_error', 'Could not join the room')();
        return;
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}
